/**
 * Hook to compose and send email (raw or from template) via Gobodo EmailApi.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  processEmailTemplate,
  buildVariablesFromContext,
  type TemplateContent,
  type EmailTemplateContext,
} from '../utils/emailTemplateUtils';
import { sendEmail, type SendEmailResponse } from '../services/emailSendService';

export interface SendEmailComposeFromTemplate {
  template: TemplateContent;
  /** Pre-built variable map (used when provided). */
  variables?: Record<string, string>;
  /** When provided, variables are derived from context and used for placeholder resolution. */
  context?: EmailTemplateContext;
}

export interface SendEmailInput {
  to: string[];
  toUserIds?: number[];
  cc?: string[];
  ccUserIds?: number[];
  bcc?: string[];
  bccUserIds?: number[];
  /** Use either subject + body (raw) or composeFromTemplate */
  subject?: string;
  body?: string;
  senderName?: string;
  senderEmail?: string;
  composeFromTemplate?: SendEmailComposeFromTemplate;
}

function resolveSubjectAndBody(input: SendEmailInput): { subject: string; body: string } {
  if (input.composeFromTemplate) {
    const { template, variables: providedVariables, context } = input.composeFromTemplate;
    const variables =
      context != null ? buildVariablesFromContext(context) : providedVariables ?? {};
    return processEmailTemplate(template, variables);
  }
  const subject = input.subject ?? '';
  const body = input.body ?? '';
  return { subject, body };
}

/**
 * Mutation to send email. Supports raw (subject + body) or template + variables.
 * Recipients can be specified by email address and/or Gobodo user ID.
 */
export function useSendEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SendEmailInput): Promise<SendEmailResponse> => {
      const { subject, body } = resolveSubjectAndBody(input);
      if (!subject.trim()) throw new Error('Subject is required');
      if (!input.to?.length && !input.toUserIds?.length) {
        throw new Error('At least one recipient (email or user ID) is required');
      }

      return sendEmail({
        to: input.to ?? [],
        toUserIds: input.toUserIds,
        cc: input.cc,
        ccUserIds: input.ccUserIds,
        bcc: input.bcc,
        bccUserIds: input.bccUserIds,
        subject,
        message: body,
        senderName: input.senderName,
        senderEmail: input.senderEmail,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
  });
}
